<script lang="ts">
  import { mdiVolumeHigh, mdiVolumeMute } from '@mdi/js';
  import { fade } from 'svelte/transition';
  import { midiNumbersBasic } from 'shared/definitions/musicTheory/notesAndAccidentals';
  import Button from '../../../../website/src/lib/components/Button.svelte';
  import StickyPianoSvgComponent from '../../uTheory.Interaction/PianoSVG/StickyPianoSVGComponent.svelte';
  import { FMSynthInstrument } from '../fmSynthInstrument';
  import Chord from '../chord';
  import Note from '../note';
  import { JustTemperament } from '../temperaments/justTemperament';
  import { EqualTemperament } from '../temperaments/equalTemperament';
  import Select from '../../../../website/src/lib/components/Select.svelte';
  import Option from '../../../../website/src/lib/components/Option.svelte';
  import Radio from '../../../../website/src/lib/components/Radio.svelte';
  import H from '../../../../website/src/lib/components/H.svelte';
  import MaterialIcon from '../../../../website/src/lib/MaterialIcon.svelte';
  import NoteSelectDropdown from '../NoteSelectDropdown.svelte';
  import { PythagoreanTemperament } from './pythagoreanTemperament';

  const noteNames = Object.values(midiNumbersBasic);

  let muted = false;
  let baseNote = noteNames[0];

  let lowestNote = new Note('C3');
  let highestNote = new Note('C5');

  const temperaments = [
    {
      name: 'Equal Temperament',
      value: new EqualTemperament(),
      requireBaseNote: false,
      automaticBaseNote: false,
    },
    {
      name: 'Just Intonation (Manual)',
      value: new JustTemperament(),
      requireBaseNote: true,
      automaticBaseNote: false,
    },
    {
      name: 'Just Intonation (Automatic)',
      value: new JustTemperament(baseNote),
      requireBaseNote: false,
      automaticBaseNote: true,
    },
    {
      name: 'Pythagorean Tuning (Manual)',
      value: new PythagoreanTemperament(baseNote),
      requireBaseNote: true,
      automaticBaseNote: false,
    },
  ] as const;

  let selectedTemperament = temperaments[0];

  let synth: FMSynthInstrument | undefined;
  if (typeof window !== 'undefined') {
    synth = new FMSynthInstrument();
  }

  let pianoSVG: StickyPianoSvgComponent;

  $: {
    if (selectedTemperament) {
      console.log('Setting temperament', selectedTemperament);
      synth?.setTemperament(
        selectedTemperament.value,
        selectedTemperament.requireBaseNote ? baseNote : automaticBaseNote,
      );
    }
  }

  let activeMidis: number[] = [];
  $: chord = Chord.fromMidis(activeMidis) || undefined;
  $: console.log(chord);
  $: lowestSoundingMidi = activeMidis.slice(0).sort((a, b) => a - b)[0];
  // eslint-disable-next-line no-nested-ternary
  $: automaticBaseNote = chord
    ? chord.root
    : lowestSoundingMidi
    ? Note.fromMidi(lowestSoundingMidi)
    : new Note('C4');
</script>

<H level={2}>Select a Tuning System:</H>

<div class="mb-4">
  {#each temperaments as temperament}
    <Radio bind:group={selectedTemperament} value={temperament}>{temperament.name}</Radio>
    {#if selectedTemperament === temperament && temperament.requireBaseNote}
      <div class="ml-6">
        Root note: <Select bind:value={baseNote} class="ml-2 border">
          {#each noteNames as noteName}
            <Option bind:value={noteName}>{noteName}</Option>
          {/each}
        </Select>
      </div>
    {:else if selectedTemperament === temperament && temperament.automaticBaseNote}
      <div class="ml-6">
        Root note: {@html automaticBaseNote.toShortString(true)}
      </div>
    {/if}
  {/each}
</div>

<div class="my-4 flex flex-wrap justify-between">
  <div class="my-1 mr-2">
    Lowest Note: <NoteSelectDropdown bind:value={lowestNote} showAccidentals={false} />
  </div>
  <div class="my-1">
    Highest Note: <NoteSelectDropdown bind:value={highestNote} showAccidentals={false} />
  </div>
</div>

<StickyPianoSvgComponent
  on:noteOn={(evt) => synth?.noteOn(evt.detail.midiNumber, 0.6)}
  on:noteOff={(evt) => synth?.noteOff(evt.detail.midiNumber)}
  {lowestNote}
  {highestNote}
  bind:activeMidis
  isInteractive
  bind:this={pianoSVG}
  height={200}
/>

<Button
  on:click={() => {
    synth?.allNotesOff();
    pianoSVG?.allNotesOff();
  }}>All notes off</Button
>

<Button
  on:click={() => {
    muted = !muted;
    synth?.setMuted(muted);
  }}
>
  {#if muted}
    <span in:fade|local><MaterialIcon path={mdiVolumeMute} label="Muted" size="1em" /></span>
  {:else}
    <span in:fade|local><MaterialIcon path={mdiVolumeHigh} label="Unmuted" size="1em" /></span>
  {/if}
</Button>
